import { z } from "zod";
import { storedTrustSchema } from "./knowledge.ts";
import { messageIdSchema, projectIdSchema } from "./projects.ts";
import { wikiMarkdownSchema, wikiPageTypeSchema } from "./wiki.ts";

// ── Wiki suggestions (openspec change `wiki-suggestions`) ───────────────────────────────────

export const wikiSuggestionIdSchema = z.string().brand<"WikiSuggestionId">();
export type WikiSuggestionId = z.infer<typeof wikiSuggestionIdSchema>;

/** A suggestion's title: one plain line (design §4). */
export const WIKI_SUGGESTION_TITLE_MAX = 120;
/** A suggestion's reason: one plain sentence. */
export const WIKI_SUGGESTION_REASON_MAX = 200;
/** Pages one suggestion holds: the wiki tool's cap per turn. */
export const WIKI_SUGGESTION_PAGES_MAX = 8;

/** `turn`: the post-turn pass; `tool`: the agent's `propose_wiki_page` calls. */
export const wikiSuggestionSourceSchema = z.enum(["turn", "tool"]);
export const wikiSuggestionKindSchema = z.enum(["new", "update"]);

/** One page of a suggestion, as its creator sees it. */
export const wikiSuggestionPageViewSchema = z.object({
  title: z.string(),
  kind: wikiSuggestionKindSchema,
  /** The edited page's current title, when the caller can read it (`update`). */
  pageTitle: z.string().nullable(),
  pageType: wikiPageTypeSchema.optional(),
  /**
   * The agent wrote this page (`tool`): its Markdown is fetched on disclosure
   * (`GET …/wiki-suggestions/:id/pages/:index`), never sent with the conversation.
   */
  written: z.literal(true).optional(),
});
export type WikiSuggestionPageView = z.infer<typeof wikiSuggestionPageViewSchema>;

/** A suggestion under an answer, until accepted, dismissed or expired (7 days). */
export const wikiSuggestionViewSchema = z.object({
  id: wikiSuggestionIdSchema,
  messageId: messageIdSchema,
  source: wikiSuggestionSourceSchema,
  /** Why it is worth keeping; null for the agent's own pages. */
  reason: z.string().nullable(),
  pages: z.array(wikiSuggestionPageViewSchema).min(1).max(WIKI_SUGGESTION_PAGES_MAX),
  trust: storedTrustSchema,
  sourceCount: z.number().int().nonnegative(),
  expiresAt: z.string(),
  /**
   * Why "Draft it" would be refused now: the setting is off, the project is frozen, or the
   * creator no longer holds `wiki.draft`. Absent: it may be drafted. Dismiss is always offered.
   */
  blocked: z.enum(["disabled", "frozen", "role"]).optional(),
});
export type WikiSuggestionView = z.infer<typeof wikiSuggestionViewSchema>;

/**
 * The suggestion streamed after an answer, before `finish`; clients without it ignore it. On
 * reload, pending suggestions come with the conversation (`wikiSuggestions`).
 */
export const WIKI_SUGGESTION_DATA_NAME = "wiki-suggestion";
export const WIKI_SUGGESTION_PART = `data-${WIKI_SUGGESTION_DATA_NAME}` as const;
export const WIKI_SUGGESTION_PART_ID = "wiki-suggestion";
export const wikiSuggestionPartSchema = z.object({
  type: z.literal(WIKI_SUGGESTION_PART),
  id: z.literal(WIKI_SUGGESTION_PART_ID),
  data: wikiSuggestionViewSchema,
});
export type WikiSuggestionPart = z.infer<typeof wikiSuggestionPartSchema>;

/** One page the agent wrote, as its creator opens it: rendered through the safe Markdown renderer. */
export const wikiSuggestionPageContentSchema = z.object({ markdown: wikiMarkdownSchema });
export type WikiSuggestionPageContent = z.infer<typeof wikiSuggestionPageContentSchema>;

/** `201` (created) or `200` (already accepted): the change set in the project's review queue. */
export const acceptedWikiSuggestionSchema = z.object({
  changeSetId: z.string(),
  projectId: projectIdSchema,
});
export type AcceptedWikiSuggestion = z.infer<typeof acceptedWikiSuggestionSchema>;
